import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n';
import type { DocumentSummary } from './DocumentCard';

interface DocumentDetail extends DocumentSummary {
  participant_id: number;
  source_file_id: number;
  md5: string;
  mime: string | null;
  full_ocr_text: string | null;
  ingest_error: string | null;
  pages: Array<{
    page_no: number;
    width: number | null;
    height: number | null;
    has_image: boolean;
    text_snippet: string | null;
  }>;
}

interface Props {
  doc: DocumentSummary | null;
  onClose: () => void;
}

export function DocumentPreviewModal({ doc, onClose }: Props) {
  const { t } = useT();
  const [detail, setDetail] = useState<DocumentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!doc) {
      setDetail(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .getDocument(doc.id)
      .then((d) => setDetail(d as DocumentDetail))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [doc]);

  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, onClose]);

  if (!doc) return null;

  const displayName = doc.original_filename || doc.filename;
  const blobUrl = api.documentBlobUrl(doc.id);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--s-16)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          width: 'min(1400px, 100%)',
          height: 'min(900px, 100%)',
          borderRadius: 'var(--r-8)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 'var(--s-12) var(--s-16)',
            borderBottom: '1px solid var(--line)',
            gap: 'var(--s-12)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 'var(--t-15)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={displayName}
            >
              {displayName}
            </div>
            {detail?.doc_type && (
              <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                {detail.doc_type}
              </div>
            )}
          </div>
          <a
            href={blobUrl}
            download={displayName}
            style={{
              padding: 'var(--s-6) var(--s-12)',
              fontSize: 'var(--t-12)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r1)',
              textDecoration: 'none',
              color: 'var(--ink-1)',
              background: 'var(--ink-9)',
            }}
          >
            {t('doc.download')}
          </a>
          <button
            onClick={onClose}
            style={{
              padding: 'var(--s-6) var(--s-12)',
              fontSize: 'var(--t-12)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r1)',
              background: 'var(--ink-9)',
              cursor: 'pointer',
            }}
          >
            {t('common.close')}
          </button>
        </header>

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div
            style={{
              flex: '1 1 60%',
              background: 'var(--ink-9)',
              borderRight: '1px solid var(--line)',
            }}
          >
            <iframe
              src={blobUrl}
              title={displayName}
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          </div>

          <aside
            style={{
              flex: '1 1 40%',
              overflowY: 'auto',
              padding: 'var(--s-16)',
              minWidth: 320,
            }}
          >
            {loading && <div style={{ color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
            {error && (
              <div className="badge badge-err" style={{ display: 'inline-block' }}>
                {error}
              </div>
            )}
            {detail && (
              <>
                {detail.summary && (
                  <section style={{ marginBottom: 'var(--s-16)' }}>
                    <h4 style={{ margin: '0 0 var(--s-6)', fontSize: 'var(--t-12)', color: 'var(--ink-2)' }}>
                      {t('doc.summary')}
                    </h4>
                    <p style={{ margin: 0, fontSize: 'var(--t-12)', color: 'var(--ink-1)' }}>
                      {detail.summary}
                    </p>
                  </section>
                )}

                {detail.detected_markers && (
                  <section style={{ marginBottom: 'var(--s-16)' }}>
                    <h4 style={{ margin: '0 0 var(--s-6)', fontSize: 'var(--t-12)', color: 'var(--ink-2)' }}>
                      {t('doc.detectedMarkers')}
                    </h4>
                    <table style={{ fontSize: 'var(--t-11)', width: '100%' }}>
                      <tbody>
                        {Object.entries(detail.detected_markers).map(([k, v]) => {
                          if (v == null || (Array.isArray(v) && v.length === 0)) return null;
                          return (
                            <tr key={k}>
                              <td style={{ color: 'var(--ink-3)', padding: '2px 8px 2px 0', verticalAlign: 'top' }}>
                                {k}
                              </td>
                              <td style={{ color: 'var(--ink-1)' }}>
                                {Array.isArray(v) ? v.join(', ') : String(v)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </section>
                )}

                {detail.full_ocr_text && (
                  <section>
                    <h4 style={{ margin: '0 0 var(--s-6)', fontSize: 'var(--t-12)', color: 'var(--ink-2)' }}>
                      {t('doc.ocrText', { n: detail.full_ocr_text.length.toLocaleString() })}
                    </h4>
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 'var(--t-11)',
                        color: 'var(--ink-2)',
                        background: 'var(--ink-9)',
                        padding: 'var(--s-8)',
                        borderRadius: 'var(--r1)',
                        maxHeight: 360,
                        overflowY: 'auto',
                      }}
                    >
                      {detail.full_ocr_text}
                    </pre>
                  </section>
                )}

                {detail.ingest_error && (
                  <section style={{ marginTop: 'var(--s-16)' }}>
                    <h4 style={{ margin: '0 0 var(--s-6)', fontSize: 'var(--t-12)', color: 'var(--err)' }}>
                      {t('doc.ingestError')}
                    </h4>
                    <pre
                      style={{
                        fontSize: 'var(--t-10)',
                        color: 'var(--err)',
                        background: 'var(--ink-9)',
                        padding: 'var(--s-8)',
                        borderRadius: 'var(--r1)',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {detail.ingest_error}
                    </pre>
                  </section>
                )}
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
